<template>
  <Modal
    v-model:visible="isVisible"
    :header="header"
    class="edit-driver-contact-modal"
    @keypress.esc="onCancel"
    @keypress.enter="onSave"
  >
    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      :input-id="`${uid}-driver-name`"
      :label="t('common.name')"
      :errors="form.name.$errors"
      class="field"
    >
      <InputText
        :id="inputId"
        v-model.trim="v$.name.$model"
        v-bind="ariaProps"
        :class="inputClasses"
        :invalid="invalid"
        autofocus
        :placeholder="t('common.name')"
      />
    </InputWrapper>

    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      :input-id="`${uid}-driver-phone`"
      :label="t('common.phone')"
      :errors="form.phone.$errors"
      class="field"
    >
      <ClearableInputMask
        :id="inputId"
        v-model.trim="v$.phone.$model"
        v-bind="ariaProps"
        :class="inputClasses"
        :invalid="invalid"
        :label="t('common.phone')"
        mask="(999) 999-9999"
      />
    </InputWrapper>

    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      :input-id="`${uid}-driver-email`"
      :label="t('common.email')"
      :errors="form.email.$errors"
      class="field"
    >
      <InputText
        :id="inputId"
        v-model.trim="v$.email.$model"
        v-bind="ariaProps"
        :class="inputClasses"
        :invalid="invalid"
        type="email"
        :placeholder="t('common.email')"
      />
    </InputWrapper>

    <InputWrapper
      v-slot="{ ariaProps, inputId }"
      :input-id="`${uid}-trailer-number`"
      :label="t('shipments.trailerNumber')"
      class="field"
      optional
    >
      <InputText
        :id="inputId"
        v-model.trim="internalValue.trailerNumber"
        v-bind="ariaProps"
        :placeholder="t('shipments.trailerNumber')"
      />
    </InputWrapper>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="onCancel"
      />
      <RygenButton
        mode="primary"
        icon="pi pi-save"
        :label="t('common.save')"
        :loading="driverContact.loading"
        :disabled="v$.$invalid"
        @click="onSave"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  matches,
  required,
  useUid,
  useValidations,
} from '@rygen/rygen-ui/composables';
import { ErrorResponse, FieldError, Loadable } from '@rygen/rygen-ui/model';

import { DriverContact } from '@/model';

import ClearableInputMask from '@/components/form/ClearableInputMask.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import InputText from 'primevue/inputtext';

const PHONE_OR_EMAIL_VALIDATION_KEY = 'driverContact.phoneOrEmailValid';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  driverContact: {
    type: Object as PropType<Loadable<DriverContact>>,
    default: () => ({ loading: false }),
  },
});
const { driverContact } = toRefs(props);
const emit = defineEmits<{
  (event: 'cancel'): void;
  (event: 'save', driverContact: DriverContact): void;
}>();

const { t } = useI18n();
const { uid } = useUid('edit-driver-contact-modal');

const internalValue: Ref<DriverContact> = ref(
  new DriverContact(driverContact.value.data),
);
const rules = {
  name: {
    required: required(t('common.name')),
  },
  phone: {},
  email: {
    // eslint-disable-next-line no-useless-escape
    matches: matches(/^.+@([\w-]+\.)+[\w-]{2,}$/, t('common.email')),
  },
};
// I know `form` is deprecated, but I can't get server errors to work otherwise. I hate vuelidate
const { externalResults, form, setServerErrors, v$, validate } = useValidations(
  internalValue,
  rules,
  uid,
);

const header = computed(() => {
  return internalValue.value?.id
    ? t('common.editParam', { param: t('shipments.driverContact') })
    : t('common.addParam', { param: t('shipments.driverContact') });
});

const onCancel = () => {
  isVisible.value = false;
  emit('cancel');
};

const validatePhoneOrEmailIsPresent = () => {
  if (internalValue.value.phone || internalValue.value.email) {
    return true;
  }

  externalResults.value = {
    ...externalResults.value,
    phone: [t('validations.editDriverContactModal.oneOfPhoneOrEmail')],
  };

  return false;
};

const validatePhoneFormat = () => {
  const phone = internalValue.value.phone;
  if (!phone || /^\(\d{3}\) \d{3}-\d{4}$/.test(phone)) {
    return true;
  }

  externalResults.value = {
    ...externalResults.value,
    phone: [t('validations.editDriverContactModal.phoneFormat')],
  };

  return false;
};

const validateManualConditions = (): boolean => {
  const isPhoneOrEmailPresent = validatePhoneOrEmailIsPresent();
  const isPhoneFormattedCorrectly = validatePhoneFormat();
  return isPhoneOrEmailPresent && isPhoneFormattedCorrectly;
};

const onSave = async () => {
  const areAutoValidationsValid = await validate();
  const areManualValidationsValid = validateManualConditions();
  if (!areAutoValidationsValid || !areManualValidationsValid) {
    return;
  }

  if (!internalValue.value.phone) {
    internalValue.value.phone = undefined;
  }
  emit('save', internalValue.value);
};

const normalizePhoneErrors = (
  errorResponse?: ErrorResponse,
): ErrorResponse | undefined => {
  if (errorResponse?.errorDetails?.fieldErrors) {
    const phoneOrEmailValidationError = errorResponse.errorDetails.fieldErrors[
      PHONE_OR_EMAIL_VALIDATION_KEY
    ] as FieldError | undefined;

    // add `phoneOrEmailValidationError` to any existing phone validation errors
    if (phoneOrEmailValidationError) {
      const initialPhoneErrors =
        errorResponse.errorDetails.fieldErrors.phone ?? [];
      const phoneErrors = !Array.isArray(initialPhoneErrors)
        ? [initialPhoneErrors]
        : initialPhoneErrors;
      const filteredPhoneErrors = phoneErrors.filter(
        (e) =>
          typeof e === 'string' || e.field !== PHONE_OR_EMAIL_VALIDATION_KEY,
      );

      return {
        ...errorResponse,
        errorDetails: {
          ...errorResponse.errorDetails,
          fieldErrors: {
            ...errorResponse.errorDetails.fieldErrors,
            phone: [
              ...(filteredPhoneErrors as FieldError[]),
              phoneOrEmailValidationError,
            ],
          },
        },
      };
    }
  }

  return errorResponse;
};

const reset = () => {
  v$.value.$reset();
  v$.value.$clearExternalResults();
};

watch(
  isVisible,
  (newVal: boolean) => {
    if (!newVal) {
      return;
    }

    internalValue.value = new DriverContact(driverContact.value.data);
    reset();
  },
  { immediate: true },
);

watch(
  () => driverContact.value.data,
  (newVal: DriverContact | undefined) => {
    internalValue.value = new DriverContact(newVal);
    reset();
  },
  { deep: true, immediate: true },
);

watch(
  () => driverContact.value.error,
  (newVal: any) => {
    if (newVal) {
      const errorResponse = normalizePhoneErrors(newVal.response?.data);
      if (errorResponse) {
        setServerErrors(errorResponse);
      }
    }
  },
  { deep: true },
);
</script>
